/**
 * Le **champ de texte de filtre** — la même pilule, autour d'une saisie libre.
 *
 * Tous les champs d'une rangée de filtres ne sont pas des listes fermées : une
 * référence d'entité est un `UUID` ou une clé que le journal a écrite, un motif
 * de consultation est une phrase que l'auditeur compose. Les offrir en liste
 * inventerait une fermeture qui n'existe pas — c'est l'erreur inverse de celle
 * que le porteur a relevée, et elle coûte autant.
 *
 * Mais la **forme**, elle, est la même : le dessin ne pose pas trois blocs
 * empilés au-dessus de sa table, il pose **une rangée de pilules**. Un champ de
 * texte qui garderait sa boîte de formulaire de 48 points au milieu de pilules
 * de 32 casserait la rangée à lui seul. Ce module habille donc la saisie libre
 * de la même pilule, sans chevron : il n'y a pas de panneau à ouvrir, et un
 * chevron qui n'ouvre rien est une affordance qui ment.
 *
 * **Ce n'est pas le champ empilé.** `FormShell` continue de rendre un
 * `.form__label` et un `.form__input` quand la disposition est empilée — un
 * formulaire de connexion ou une demande de clôture ne sont pas des filtres, et
 * une pilule de 32 points y serait une densité qu'aucun dessin ne demande.
 */

import type { ReactNode } from 'react';

import type { UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

/** Ce que le champ de texte attend. */
export interface FilterTextFieldProps {
  /** L'identifiant du champ, celui que le libellé de la pilule désigne. */
  readonly controlId: string;
  /** Clé de traduction du libellé. */
  readonly labelKey: string;
  /** Le champ est-il en erreur ? */
  readonly invalid?: boolean;
  /** L'identifiant du message d'erreur, pour `aria-describedby`. */
  readonly describedBy?: string;
  /** La liaison du formulaire — `register(nom, …)` de React Hook Form. */
  readonly registration: UseFormRegisterReturn;
}

/** La saisie libre, à la forme de la rangée de filtres. */
export function FilterTextField({
  controlId,
  labelKey,
  invalid,
  describedBy,
  registration,
}: FilterTextFieldProps): ReactNode {
  const { t } = useTranslation();
  return (
    <span className="filter-pill filter-pill--text">
      <label className="filter-pill__label" htmlFor={controlId}>
        {t(labelKey)}
      </label>
      <input
        className="filter-pill__value"
        id={controlId}
        type="text"
        aria-invalid={invalid === true ? true : undefined}
        aria-describedby={describedBy}
        {...registration}
      />
    </span>
  );
}
